/**
 * PrintCertificatePage — renders a full certificate with template body,
 * school branding, signatures, and QR code. Auto-opens the print dialog.
 *
 * Route: /certificates/:id/print  (admin auth required — token from localStorage)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Replace {{placeholders}} in a template body with values from the certificate
function renderBody(body, cert) {
  const values = {
    recipient_name: cert.recipient_name || '',
    program: cert.recipient_data?.program || cert.recipient_data?.department || '',
    department: cert.recipient_data?.department || '',
    graduation_year: cert.recipient_data?.graduation_year || '',
    issue_date: cert.issue_date || '',
    serial: cert.serial || '',
    institution: cert.school?.name || '',
    student_number: cert.recipient_data?.student_number || '',
    ...(cert.extra_fields || {}),
  };
  return (body || '').replace(/\{\{(\w+)\}\}/g, (_, k) => values[k] ?? '');
}

export default function PrintCertificatePage() {
  const { id: certId } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    (async () => {
      // Admin must have been on a school page to know the tenant_id — we read it
      // from the URL query ?tenant= or from sessionStorage fallback.
      const params = new URLSearchParams(window.location.search);
      const shareToken = params.get('token') || '';
      if (shareToken) {
        // The holder's own QR token — works without a login (student portal).
        try {
          const r = await fetch(`${API_URL}/api/public/certificates/render/${shareToken}`);
          if (!r.ok) { setErr('Could not load certificate.'); return; }
          setCert(await r.json());
        } catch {
          setErr('Network error.');
        }
        return;
      }
      const tid = params.get('tenant') || sessionStorage.getItem('currentSchoolTenantId') || '';
      setTenantId(tid);
      if (!tid) { setErr('Tenant not specified. Open the certificate from the admin table.'); return; }
      try {
        const r = await fetch(`${API_URL}/api/school/tenants/${tid}/certificates/${certId}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` },
        });
        if (!r.ok) { setErr('Could not load certificate.'); return; }
        const data = await r.json();
        setCert(data);
      } catch {
        setErr('Network error.');
      }
    })();
  }, [certId]);

  useEffect(() => {
    if (cert && !err) {
      // Small delay to let images load before triggering print
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [cert, err]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-red-600">{err}</div>;
  if (!cert) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const tpl = cert.template || {};
  const school = cert.school || {};
  const landscape = (tpl.orientation || 'landscape') === 'landscape';
  const primary = school.primary_color || '#1e3a8a';
  const qrSrc = `${API_URL}/api/public/certificates/qr/${cert.serial}.png?base_url=${encodeURIComponent(window.location.origin)}&size=6`;

  return (
    <>
      <style>{`
        @media print {
          @page { size: ${landscape ? 'A4 landscape' : 'A4 portrait'}; margin: 0; }
          body { margin: 0; }
          .no-print { display: none !important; }
        }
        body { background: #f1f5f9; }
      `}</style>
      <div className="no-print fixed top-3 right-3 z-50">
        <button onClick={() => window.print()} className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md shadow">Re-print</button>
      </div>
      <div
        className="cert-page mx-auto my-6 bg-white shadow-2xl relative"
        style={{
          width: landscape ? '297mm' : '210mm',
          minHeight: landscape ? '210mm' : '297mm',
          padding: '20mm',
          border: `12px double ${primary}`,
          fontFamily: '"Georgia", "Times New Roman", serif',
        }}
        data-testid="printable-certificate"
      >
        {tpl.watermark_text && (
          <div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ fontSize: '120px', color: primary, opacity: 0.06, transform: 'rotate(-30deg)', fontWeight: 'bold' }}
          >
            {tpl.watermark_text}
          </div>
        )}

        <header className="text-center mb-6">
          {school.logo_url && <img src={school.logo_url} alt="" className="h-20 mx-auto mb-2" />}
          <h1 className="text-3xl font-bold" style={{ color: primary }}>{school.name || ''}</h1>
          {school.address && <p className="text-sm text-slate-500">{school.address}</p>}
          <h2 className="text-2xl font-semibold mt-8" style={{ color: primary }}>{tpl.title || 'Certificate'}</h2>
        </header>

        <div
          className="text-center text-lg leading-relaxed mx-auto"
          style={{ maxWidth: '80%' }}
          dangerouslySetInnerHTML={{ __html: renderBody(tpl.body_html, cert) }}
        />

        <footer className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-4 items-end">
          <div className="col-span-2">
            {(tpl.signatures || []).map((s, i) => (
              <div key={i} className="inline-block mr-8 text-center">
                {s.image_url && <img src={s.image_url} alt="" className="h-12 mx-auto" />}
                <div className="border-t border-slate-800 w-40 mx-auto mt-1 pt-1 text-xs">
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-slate-500">{s.role}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 mt-4">Issued: <strong>{cert.issue_date}</strong></p>
            <p className="text-xs text-slate-500">Serial: <code className="font-mono">{cert.serial}</code></p>
          </div>
          <div className="text-center">
            <img src={qrSrc} alt="Verify QR" className="h-24 w-24 mx-auto" />
            <p className="text-[10px] text-slate-500 mt-1">Scan to verify</p>
          </div>
        </footer>
      </div>
    </>
  );
}
